/**
 * components/landing/platform/sections/BusinessOps.jsx — §3.7.
 *
 * Grid of business-ops capabilities — billing, subscription lifecycle,
 * customer + license management, roles, notifications, analytics,
 * revenue, workspace admin.  The point of this section is to say
 * "Kalki Ai Cloud isn’t just software — it’s the operating backbone".
 */

import React from 'react'
import {
  CreditCard, Repeat, UsersRound, KeyRound, ShieldCheck, BellRing,
  PieChart, TrendingUp, Cog, Sparkles,
} from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { BUSINESS_OPS } from '../data'

const ICONS = { CreditCard, Repeat, UsersRound, KeyRound, ShieldCheck, BellRing, PieChart, TrendingUp, Cog }
const TONE = {
  orange: { hex: '#FFC107', rgb: '243,112,33' },
  green:  { hex: '#22c55e', rgb: '34,197,94' },
  blue:   { hex: '#3b82f6', rgb: '59,130,246' },
  purple: { hex: '#a855f7', rgb: '168,85,247' },
  cyan:   { hex: '#22d3ee', rgb: '34,211,238' },
}

export default function BusinessOps() {
  return (
    <section className="relative py-24 md:py-28">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>Business Operations</Eyebrow>
          <SectionHeading
            align="center"
            title={<>The <span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">operating backbone</span> for your business.</>}
            subtitle="Kalki Ai Cloud isn’t just trading software — it’s the CRM, billing, license and analytics stack that runs the business behind it."
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          {BUSINESS_OPS.map((o) => {
            const tone = TONE[o.tone] || TONE.orange
            const Icon = ICONS[o.iconKey] || Sparkles
            return (
              <div key={o.id} className="relative rounded-2xl border border-white/10 bg-white/[0.02] p-5 backdrop-blur-xl overflow-hidden transition-all hover:border-white/20 hover:bg-white/[0.03] hover:-translate-y-[1px]">
                <div className="pointer-events-none absolute inset-0 opacity-50"
                  style={{ background: `radial-gradient(80% 40% at 20% 0%, rgba(${tone.rgb},0.08), transparent 70%)` }} />
                <div className="relative flex flex-col gap-3">
                  <span
                    className="inline-flex h-10 w-10 items-center justify-center rounded-xl border"
                    style={{ borderColor: `rgba(${tone.rgb},0.35)`, background: `rgba(${tone.rgb},0.08)`, color: tone.hex }}
                  >
                    <Icon strokeWidth={1.8} style={{ width: 18, height: 18 }} />
                  </span>
                  <div>
                    <h4 className="text-base font-semibold text-white leading-tight">{o.label}</h4>
                    <p className="text-sm text-zinc-400 leading-relaxed mt-1">{o.body}</p>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        <blockquote className="mx-auto max-w-3xl text-center rounded-2xl border border-brand-gold/25 bg-brand-gold/[0.06] px-6 py-5 text-[15px] italic text-zinc-100 leading-relaxed">
          “You’re not just getting trading software — you are getting the operating backbone for your business.”
        </blockquote>
      </Container>
    </section>
  )
}
